import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { IMG } from "@/lib/images";

const TILES = [
  { img: IMG.BIKE_WATERFRONT, pos: "50% 55%", label: "The bike that started it all", note: "Built around thousands of kilometres on a real Harley-Davidson Road Glide 3. Not a design brief." },
  { img: IMG.INTERIOR_LOGO, pos: "50% 35%", label: "Build quality you can hold", note: "Direct embroidered RR. Sewn-on woven label. ROADRIG orange that doesn't fade." },
  { img: IMG.TECH_PACK,     pos: "0% 0%",   label: "Every detail documented",   note: "Nine engineering pages. Every dimension, every component. No improvising." },
];

const PILLARS = [
  { title: "Not the cheapest", note: "Price first kills quality. Always." },
  { title: "The right people", note: "Care about the craft. Argue with us." },
  { title: "The right quality", note: "Tested. Re-tested. Then tested again." },
  { title: "The right communication", note: "Honest timelines. No silence." },
];

export const FactoryDevelopment = () => {
  return (
    <section id="factory" data-testid="factory-section" className="relative py-28 md:py-36 bg-[#0A0A0C]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="section-mark" />
              <span className="overline">Factory Development</span>
            </div>
            <h2 className="h-section text-white text-4xl sm:text-5xl lg:text-6xl">
              Finding the right <br />
              <span className="text-[#F47A20]">partner matters.</span>
            </h2>
          </div>
          <p className="text-white/70 max-w-md">
            Not the cheapest. The right people. The right quality. The right communication.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mb-14">
          {TILES.map((t, i) => (
            <motion.div
              key={t.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              className="group relative aspect-[4/5] overflow-hidden rounded-sm border border-white/10 bg-[#141417]"
              data-testid={`factory-tile-${i}`}
            >
              <img
                src={t.img}
                alt={t.label}
                loading="lazy"
                decoding="async"
                style={{ objectPosition: t.pos }}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/40 to-transparent" />
              <div className="absolute top-4 left-4 serial text-[10px] text-[#F47A20]">0{i + 1} / 03</div>
              <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                <div className="h-display text-xl md:text-2xl text-white mb-2 leading-tight">{t.label}</div>
                <p className="text-sm text-white/65 leading-relaxed">{t.note}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {PILLARS.map((p, i) => (
            <div
              key={p.title}
              data-testid={`factory-pillar-${i}`}
              className="flex items-start gap-4 p-4 bg-[#141417] border border-white/5 hover:border-[#F47A20]/30 transition-colors rounded-sm"
            >
              <CheckCircle2 size={18} className="text-[#F47A20] mt-1 flex-shrink-0" strokeWidth={1.6} />
              <div>
                <div className="text-white font-semibold">{p.title}</div>
                <div className="text-white/60 text-sm">{p.note}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
